'use client';

import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import i18next from '@/lib/i18n';

const t = (k: string): string => String(i18next.t(k));
import {
  SortableContext,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { SortableGridItem } from './sortable-grid-item';
import { XIcon, Loader2Icon, PlusIcon, UploadIcon, LibraryBigIcon, UserSquare } from 'lucide-react';
import { useMemo, type ComponentProps, type ReactNode } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

const PICKER_MENU_CN = 'fm-toolbar-surface bg-sidebar/95 backdrop-blur-xl border-sidebar-border shadow-none rounded-xl min-w-[160px] p-1 z-[200]';
const PICKER_ITEM_CN = 'rounded-lg py-2 px-2.5 focus:bg-primary/10 focus:text-primary transition-colors cursor-pointer text-xs font-medium';

export type MediaGridItem = {
  id: string;
  source: 'upstream' | 'inline';
  url?: string;
  name?: string;
  sourceId?: string;
  attId?: string;
  mediaType?: 'image' | 'video' | 'audio';
  previewUrl?: string;
};

type DraggableMediaGridProps = {
  items: MediaGridItem[];
  sensors: ComponentProps<typeof DndContext>['sensors'];
  onReorder: (event: DragEndEvent) => void;
  /** Render the main content inside the background layer (images, videos, pending icons). */
  renderContent: (item: MediaGridItem, index: number) => ReactNode;
  /** Render extra overlays on top of the background layer (play buttons, click handlers). */
  renderOverlay?: (item: MediaGridItem, index: number) => ReactNode;
  /** Bottom label text for each item. */
  getLabel: (item: MediaGridItem, index: number) => string;
  /** Whether an item is "active" (default: all active). Inactive items get opacity + grayscale. */
  isActive?: (item: MediaGridItem) => boolean;
  /** Called when the delete button is clicked on an inline item. */
  onRemoveInline?: (attId: string) => void;
  /** Called when the X button is clicked on an upstream item (should disconnect the edge). */
  onDisconnectUpstream?: (sourceId: string) => void;
  disabled?: boolean;
  canUpload?: boolean;
  isUploading?: boolean;
  onUpload?: () => void;
  uploadTitle?: string;
  uploadPlaceholderLabel?: string;
  onPickFromLibrary?: () => void;
  pickFromLibraryTitle?: string;
  /** 主体库选择回调（可选）。仅在传入时菜单中才会显示「从主体库加载」一项。 */
  onPickFromSubjectLibrary?: () => void;
  pickFromSubjectLibraryTitle?: string;
  /** Extra slots rendered after items but before the upload button (guided slots, etc.). */
  extraSlots?: ReactNode;
};

export function DraggableMediaGrid({
  items,
  sensors,
  onReorder,
  renderContent,
  renderOverlay,
  getLabel,
  isActive,
  onRemoveInline,
  onDisconnectUpstream,
  disabled,
  canUpload,
  isUploading,
  onUpload,
  uploadTitle,
  uploadPlaceholderLabel,
  onPickFromLibrary,
  pickFromLibraryTitle,
  onPickFromSubjectLibrary,
  pickFromSubjectLibraryTitle,
  extraSlots,
}: DraggableMediaGridProps) {
  const sortableItemIds = useMemo(() => items.map((i) => i.id), [items]);

  const uploadPlaceholderOverlay = uploadPlaceholderLabel ? (
    <div className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 flex h-[24px] items-center justify-center overflow-hidden rounded-b-lg bg-black/50 px-0.5 text-center text-[9px] leading-tight text-white">
      <span className="line-clamp-2 break-all">{uploadPlaceholderLabel}</span>
    </div>
  ) : null;

  const renderUploadButton = (enableDirectUpload: boolean) => (
    <button
      type="button"
      className={`relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center transition-colors ${disabled ? 'opacity-30 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/30'}`}
      onClick={!disabled && enableDirectUpload ? onUpload : undefined}
      title={uploadTitle}
      disabled={disabled}
    >
      <PlusIcon size={12} className="text-muted-foreground" />
    </button>
  );

  return (
    <div className="px-0.5">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onReorder}>
        <SortableContext items={sortableItemIds} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-7 gap-1.5">
            {items.map((item, idx) => {
              const active = isActive ? isActive(item) : true;
              const label = getLabel(item, idx);
              return (
                <div key={item.id} className="relative">
                  <SortableGridItem id={item.id} disabled={disabled}>
                    <div className="relative w-full aspect-square group rounded-lg overflow-hidden" title={item.name}>
                      <div className={`absolute inset-0 bg-muted/30 overflow-hidden rounded-lg transition-all ${!active ? 'opacity-35 grayscale' : ''}`}>
                        {renderContent(item, idx)}
                      </div>
                      {renderOverlay?.(item, idx)}
                      {item.source === 'inline' && onRemoveInline && !disabled && (
                        <button
                          type="button"
                          className="absolute top-0 left-0 h-4 w-4 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-10"
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onRemoveInline(item.attId!); }}
                        >
                          <XIcon size={8} />
                        </button>
                      )}
                      {item.source === 'upstream' && onDisconnectUpstream && item.sourceId && !disabled && (
                        <button
                          type="button"
                          className="absolute top-0 left-0 h-4 w-4 bg-destructive/80 text-white rounded-br-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer z-20"
                          title={t("Disconnect")}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDisconnectUpstream(item.sourceId!); }}
                        >
                          <XIcon size={8} />
                        </button>
                      )}
                    </div>
                  </SortableGridItem>
                  <div className={`pointer-events-none absolute bottom-0 left-0 right-0 text-white text-[9px] text-center leading-tight px-0.5 rounded-b-lg z-10 h-[24px] flex items-center justify-center overflow-hidden ${active ? 'bg-black/50' : 'bg-black/30'}`}>
                    <span className="line-clamp-2 break-all">{label}</span>
                  </div>
                </div>
              );
            })}
            {extraSlots}
            {canUpload && !isUploading && (onPickFromLibrary || onPickFromSubjectLibrary) ? (
              <div className="relative">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild disabled={disabled}>
                    {renderUploadButton(false)}
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" side="bottom" className={PICKER_MENU_CN}>
                    <DropdownMenuItem className={PICKER_ITEM_CN} onClick={onUpload}>
                      <UploadIcon size={14} className="mr-1.5" />
                      {uploadTitle}
                    </DropdownMenuItem>
                    {onPickFromLibrary ? (
                      <DropdownMenuItem className={PICKER_ITEM_CN} onClick={onPickFromLibrary}>
                        <LibraryBigIcon size={14} className="mr-1.5" />
                        {pickFromLibraryTitle}
                      </DropdownMenuItem>
                    ) : null}
                    {onPickFromSubjectLibrary ? (
                      <DropdownMenuItem className={PICKER_ITEM_CN} onClick={onPickFromSubjectLibrary}>
                        <UserSquare size={14} className="mr-1.5" />
                        {pickFromSubjectLibraryTitle}
                      </DropdownMenuItem>
                    ) : null}
                  </DropdownMenuContent>
                </DropdownMenu>
                {uploadPlaceholderOverlay}
              </div>
            ) : canUpload && !isUploading ? (
              <div className="relative">
                {renderUploadButton(true)}
                {uploadPlaceholderOverlay}
              </div>
            ) : null}
            {isUploading && (
              <div className="relative w-full aspect-square bg-muted/10 border border-dashed border-muted-foreground/25 rounded-lg flex items-center justify-center">
                <Loader2Icon size={12} className="animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
